'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryImage {
  before: string;
  beforeAlt: string;
  after: string;
  afterAlt: string;
  title: string;
  description: string;
}

interface BeforeAfterGalleryProps {
  images: GalleryImage[];
}

export default function BeforeAfterGallery({ images }: BeforeAfterGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBefore, setShowBefore] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setShowBefore(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setShowBefore(true);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="h-96 bg-muted rounded-lg"></div>
      </div>
    );
  }

  const currentImage = images[currentIndex];

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border">
      <div className="bg-primary text-primary-foreground px-6 py-4">
        <h3 className="text-xl font-heading font-bold flex items-center gap-2">
          <Icon name="PhotoIcon" size={24} />
          Galerie Avant/Après
        </h3>
      </div>

      <div className="p-6">
        <div className="relative h-96 rounded-lg overflow-hidden mb-4">
          <AppImage
            src={showBefore ? currentImage.before : currentImage.after}
            alt={showBefore ? currentImage.beforeAlt : currentImage.afterAlt}
            className="w-full h-full object-cover"
          />

          <div className="absolute top-4 left-4 bg-foreground/80 text-background px-4 py-2 rounded-lg font-semibold">
            {showBefore ? 'Avant' : 'Après'}
          </div>

          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 hover:bg-card rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
            aria-label="Image précédente"
          >
            <Icon name="ChevronLeftIcon" size={24} className="text-foreground" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 hover:bg-card rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
            aria-label="Image suivante"
          >
            <Icon name="ChevronRightIcon" size={24} className="text-foreground" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={() => setShowBefore(true)}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 ${
              showBefore
                ? 'bg-primary text-primary-foreground'
                : 'bg-surface text-foreground hover:bg-muted'
            }`}
          >
            Avant
          </button>
          <button
            onClick={() => setShowBefore(false)}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 ${
              !showBefore
                ? 'bg-primary text-primary-foreground'
                : 'bg-surface text-foreground hover:bg-muted'
            }`}
          >
            Après
          </button>
        </div>

        <div className="text-center">
          <h4 className="text-lg font-heading font-bold text-foreground mb-2">
            {currentImage.title}
          </h4>
          <p className="text-muted-foreground text-sm">
            {currentImage.description}
          </p>
        </div>

        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setShowBefore(true);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-muted'
              }`}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}